import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { type DateTime } from "luxon";

/**
 * MUI's AdapterLuxon uses Luxon `startOf('week')` / `endOf('week')` in a few places.
 * In Luxon (v3.4+), locale-aware weeks require `{ useLocaleWeeks: true }`.
 *
 * Without this, some environments will default to ISO weeks (Mon-first) even when
 * Intl.Locale().weekInfo says Sunday-first for en-US.
 */
export class AdapterLuxonLocaleWeeks extends AdapterLuxon {
  constructor(...args: ConstructorParameters<typeof AdapterLuxon>) {
    super(...args);

    this.startOfWeek = (value: DateTime) => value.startOf("week", { useLocaleWeeks: true });
    this.endOfWeek = (value: DateTime) => value.endOf("week", { useLocaleWeeks: true });

    this.getWeekArray = (value: DateTime) => {
      const { setLocaleToValue } = this as unknown as { setLocaleToValue: (v: DateTime) => DateTime };
      const cleanValue = setLocaleToValue(value);

      const start = cleanValue.startOf("month").startOf("week", { useLocaleWeeks: true });
      const end = cleanValue.endOf("month").endOf("week", { useLocaleWeeks: true });
      const days = end.diff(start, "days").as("days");

      const weeks: DateTime[][] = [];
      new Array(Math.round(days))
        .fill(0)
        .map((_, i) => i)
        .map((day) => start.plus({ days: day }))
        .forEach((v, i) => {
          if (i === 0 || (i % 7 === 0 && i > 6)) {
            weeks.push([v]);
            return;
          }
          const lastWeek = weeks[weeks.length - 1];
          if (!lastWeek) {
            weeks.push([v]);
            return;
          }
          lastWeek.push(v);
        });

      return weeks;
    };
  }
}
